"use client";

import { useRef, useCallback, useMemo } from "react";
import type { TimelineData as TimelineDataType } from "@/lib/types";
import TimelineEventCard from "./TimelineEvent";
import TimelineControls from "./TimelineControls";

const ERA_COLORS: Record<string, string> = {
  creation: "#6b21a8",
  patriarchs: "#d97706",
  exodus: "#dc2626",
  conquest: "#ea580c",
  judges: "#78350f",
  "united kingdom": "#c8a45e",
  "divided kingdom": "#7c6f4e",
  exile: "#4b5563",
  return: "#0d9488",
  "new testament": "#e8e0d0",
};

function getEraColor(eraName: string): string {
  const key = eraName.toLowerCase();
  return ERA_COLORS[key] || "#c8a45e";
}

interface TimelineViewProps {
  data: TimelineDataType;
  zoom: number;
  onZoomChange: (z: number) => void;
  searchQuery: string;
  onSearch: (q: string) => void;
}

export default function TimelineView({
  data,
  zoom,
  onZoomChange,
  searchQuery,
  onSearch,
}: TimelineViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const eraRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setEraRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) eraRefs.current.set(id, el);
    else eraRefs.current.delete(id);
  }, []);

  const handleEraJump = useCallback((eraId: string) => {
    const el = eraRefs.current.get(eraId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, []);

  // Sort events by year
  const sortedEvents = useMemo(() => {
    let events = [...data.events].sort((a, b) => a.approxYear - b.approxYear);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      events = events.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.people?.some((p) => p.toLowerCase().includes(q))
      );
    }
    return events;
  }, [data.events, searchQuery]);

  // Group events by era
  const eventsByEra = useMemo(() => {
    const map = new Map<string, typeof sortedEvents>();
    for (const event of sortedEvents) {
      const eraEvents = map.get(event.era) || [];
      eraEvents.push(event);
      map.set(event.era, eraEvents);
    }
    return map;
  }, [sortedEvents]);

  // Use era data for display order
  const sortedEras = useMemo(
    () => [...data.eras].sort((a, b) => a.startYear - b.startYear),
    [data.eras]
  );

  const baseWidth = 220 * zoom;

  return (
    <div className="relative w-full h-full" style={{ background: "#0a0e1a" }}>
      <TimelineControls
        eras={data.eras}
        zoom={zoom}
        onZoomChange={onZoomChange}
        onEraJump={handleEraJump}
        onSearch={onSearch}
      />

      <div
        ref={scrollRef}
        className="w-full h-full overflow-x-auto overflow-y-hidden pt-16"
        style={{ scrollSnapType: "x proximity" }}
      >
        <div className="flex items-center h-full" style={{ minHeight: "100%" }}>
          {/* Padding at start */}
          <div className="shrink-0 w-12" />

          {sortedEras.map((era) => {
            const eraEvents = eventsByEra.get(era.name) || [];
            const eraColor = era.color || getEraColor(era.name);
            const sectionWidth = Math.max(300, eraEvents.length * baseWidth);

            return (
              <div
                key={era.id}
                ref={(el) => setEraRef(era.id, el)}
                className="relative shrink-0 flex flex-col justify-center"
                style={{
                  width: sectionWidth,
                  height: "100%",
                  scrollSnapAlign: "start",
                }}
              >
                {/* Era background band */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, ${eraColor}08 0%, ${eraColor}15 50%, ${eraColor}08 100%)`,
                    borderLeft: `1px solid ${eraColor}22`,
                  }}
                />

                {/* Era label */}
                <div
                  className="absolute top-16 left-4 z-5"
                  style={{ color: `${eraColor}88` }}
                >
                  <p className="text-xs uppercase tracking-widest font-semibold">
                    {era.name}
                  </p>
                  <p className="text-[10px] mt-0.5 opacity-60">
                    {era.startYear < 0
                      ? `${Math.abs(era.startYear)} BC`
                      : `${era.startYear} AD`}{" "}
                    -{" "}
                    {era.endYear < 0
                      ? `${Math.abs(era.endYear)} BC`
                      : `${era.endYear} AD`}
                  </p>
                </div>

                {/* Central timeline axis */}
                <div
                  className="absolute left-0 right-0"
                  style={{
                    top: "50%",
                    height: 2,
                    background: `linear-gradient(90deg, ${eraColor}44, ${eraColor}22)`,
                  }}
                />

                {/* Events positioned above/below */}
                <div
                  className="relative flex items-center gap-2 px-4"
                  style={{ height: "100%" }}
                >
                  {eraEvents.map((event, i) => (
                    <div
                      key={event.id}
                      className="shrink-0"
                      style={{
                        alignSelf: i % 2 === 0 ? "flex-start" : "flex-end",
                        paddingTop: i % 2 === 0 ? "100px" : "0",
                        paddingBottom: i % 2 === 0 ? "0" : "100px",
                      }}
                    >
                      <TimelineEventCard
                        event={event}
                        position={i % 2 === 0 ? "above" : "below"}
                        eraColor={eraColor}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Padding at end */}
          <div className="shrink-0 w-12" />
        </div>
      </div>
    </div>
  );
}
